import { useState, useMemo } from "react";
import { Table, Popconfirm, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  EditOutlined,
  DeleteOutlined,
  SendOutlined,
  CopyOutlined,
  PaperClipOutlined,
} from "@ant-design/icons";
import { HiOutlineMail } from "react-icons/hi";
import {
  Button,
  IconButton,
  StatusIndicator,
  Pagination,
  TableSearch,
  SearchFilterModal,
  type FilterCondition,
  type FilterableField,
} from "@/components/ui";
import dayjs from "dayjs";
import type { ManualEmail } from "../types";

const filterFields: FilterableField[] = [
  {
    id: 'status',
    identifier: 'status',
    label: 'Status',
    type: 'select',
    options: [
      { label: 'Draft', value: 'draft' },
      { label: 'Sent', value: 'sent' },
    ],
  },
];

const PAGE_SIZE = 10;

type Props = {
  emails: ManualEmail[];
  eventSlug: string;
  onSend: (email: ManualEmail) => void;
  onDuplicate: (email: ManualEmail) => void;
  onDelete: (email: ManualEmail) => void;
};

const ManualEmailList = ({ emails, eventSlug, onSend, onDuplicate, onDelete }: Props) => {
  const [searchValue, setSearchValue] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "draft" | "sent">("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [filterConditions, setFilterConditions] = useState<FilterCondition[]>([]);
  const [filterNameSearch, setFilterNameSearch] = useState("");

  const filteredEmails = useMemo(() => {
    return emails.filter((email) => {
      const matchesSearch =
        email.campaignName.toLowerCase().includes(appliedSearch.toLowerCase()) ||
        email.subjectLine.toLowerCase().includes(appliedSearch.toLowerCase());
      const matchesStatus = statusFilter === "all" || email.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [emails, appliedSearch, statusFilter]);

  const paginatedEmails = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredEmails.slice(startIndex, startIndex + pageSize);
  }, [filteredEmails, currentPage, pageSize]);

  const handleEdit = (email: ManualEmail) => {
    window.location.href = `/events/${eventSlug}/messaging/email_blasts/${email.id}/edit`;
  };

  const handlePageChange = (page: number, newPageSize: number) => {
    setCurrentPage(page);
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setCurrentPage(1);
    }
  };

  const handleSearch = () => {
    setAppliedSearch(searchValue);
    setCurrentPage(1);
  };

  const handleReset = () => {
    setSearchValue("");
    setAppliedSearch("");
    setStatusFilter("all");
    setCurrentPage(1);
  };

  const handleApplyFilters = () => {
    // Apply name search
    if (filterNameSearch) {
      setAppliedSearch(filterNameSearch);
    }

    // Apply status filter from conditions
    const statusCondition = filterConditions.find(c => c.field === 'status' && c.value);
    if (statusCondition) {
      setStatusFilter(statusCondition.value as "all" | "draft" | "sent");
    }

    setCurrentPage(1);
    setFilterModalOpen(false);
  };

  const handleResetFilters = () => {
    setFilterNameSearch("");
    setFilterConditions([]);
    setAppliedSearch("");
    setStatusFilter("all");
    setCurrentPage(1);
    setFilterModalOpen(false);
  };

  const columns: ColumnsType<ManualEmail> = [
    {
      title: "Campaign Name",
      dataIndex: "campaignName",
      key: "campaignName",
      sorter: (a, b) => a.campaignName.localeCompare(b.campaignName),
    },
    {
      title: "Email Subject Line",
      dataIndex: "subjectLine",
      key: "subjectLine",
      sorter: (a, b) => a.subjectLine.localeCompare(b.subjectLine),
      ellipsis: true,
      render: (subjectLine: string, record: ManualEmail) => (
        <span className="flex items-center gap-1">
          <span className="truncate">{subjectLine}</span>
          {!!record.attachments?.length && (
            <Tooltip title={record.attachments.map((file) => file.filename).join(", ")}>
              <span className="flex shrink-0 items-center gap-0.5 text-gray-400">
                <PaperClipOutlined />
                {record.attachments.length}
              </span>
            </Tooltip>
          )}
        </span>
      ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: (a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
      render: (date: string) => dayjs(date).format("YYYY-MM-DD, hh:mmA"),
    },
    {
      title: "Sent At",
      dataIndex: "sentAt",
      key: "sentAt",
      sorter: (a, b) => {
        if (!a.sentAt) return 1;
        if (!b.sentAt) return -1;
        return dayjs(a.sentAt).unix() - dayjs(b.sentAt).unix();
      },
      render: (date?: string) => (date ? dayjs(date).format("YYYY-MM-DD, hh:mmA") : "-"),
    },
    {
      title: "Recipients",
      dataIndex: "recipientCount",
      key: "recipientCount",
      sorter: (a, b) => a.recipientCount - b.recipientCount,
      align: "center",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: ManualEmail["status"]) => (
        <StatusIndicator
          status={status === "sent" ? "success" : "inactive"}
          label={status === "sent" ? "Sent" : "Draft"}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      width: 220,
      render: (_: unknown, record: ManualEmail) => (
        <div className="flex items-center justify-center gap-1">
          <Button
            variant="secondary"
            size="small"
            icon={<SendOutlined />}
            onClick={() => onSend(record)}
            disabled={record.status === "sent"}
          >
            Send
          </Button>
          <IconButton
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            ariaLabel="Edit email"
            title="Edit"
          />
          <Popconfirm
            title="Duplicate email"
            description="Duplicate this email campaign?"
            onConfirm={() => onDuplicate(record)}
            okText="Duplicate"
            cancelText="Cancel"
          >
            <IconButton
              icon={<CopyOutlined />}
              ariaLabel="Duplicate email"
              title="Duplicate"
            />
          </Popconfirm>
          <Popconfirm
            title="Delete email"
            description="Are you sure you want to delete this email?"
            onConfirm={() => onDelete(record)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <IconButton
              icon={<DeleteOutlined />}
              ariaLabel="Delete email"
              title="Delete"
              danger
            />
          </Popconfirm>
        </div>
      ),
    },
  ];

  // Zero state
  if (emails.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="mb-6 rounded-full bg-blue-50 p-6">
          <HiOutlineMail className="text-5xl text-blue-500" />
        </div>
        <h3 className="mb-2 text-lg font-medium text-gray-900">No Manual Email Yet</h3>
        <p className="mb-6 text-sm text-gray-500">
          Get started by creating your first email campaign
        </p>
        <Button
          variant="primary"
          onClick={() => (window.location.href = `/events/${eventSlug}/messaging/email_blasts/new`)}
        >
          Create Now
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-4">
        <TableSearch
          value={searchValue}
          onChange={setSearchValue}
          onSearch={handleSearch}
          onReset={handleReset}
          onFilter={() => setFilterModalOpen(true)}
          placeholder="Search Email"
          showFilter={true}
        />
      </div>

      <Table
        columns={columns}
        dataSource={paginatedEmails}
        rowKey="id"
        className="ava-data-table"
        showSorterTooltip={false}
        pagination={false}
      />

      {filteredEmails.length > 0 && (
        <div className="mt-4">
          <Pagination
            current={currentPage}
            total={filteredEmails.length}
            pageSize={pageSize}
            onChange={handlePageChange}
            pageSizeOptions={["10", "20", "50"]}
          />
        </div>
      )}

      <SearchFilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        title="Filter Emails"
        description="Show emails that match the following..."
        fields={filterFields}
        filterConditions={filterConditions}
        onFilterConditionsChange={setFilterConditions}
        showNameSearch={true}
        nameSearchPlaceholder="Search campaign name or subject"
        nameSearchValue={filterNameSearch}
        onNameSearchChange={setFilterNameSearch}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
      />
    </div>
  );
};

export default ManualEmailList;
